import { createSelector } from 'reselect';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { selectCurrentOrganizationId } from './auth.js';

// API Actions
const DUPLICATE_PROJECT_REQUEST =
  'changesync/projectDuplications/DUPLICATE_PROJECT_REQUEST';
const DUPLICATE_PROJECT_SUCCESS =
  'changesync/projectDuplications/DUPLICATE_PROJECT_SUCCESS';
const DUPLICATE_PROJECT_FAILURE =
  'changesync/projectDuplications/DUPLICATE_PROJECT_FAILURE';
const RETRIEVE_DUPLICATIONS_REQUEST =
  'changesync/projectDuplications/RETRIEVE_DUPLICATIONS_REQUEST';
const RETRIEVE_DUPLICATIONS_SUCCESS =
  'changesync/projectDuplications/RETRIEVE_DUPLICATIONS_SUCCESS';
const RETRIEVE_DUPLICATIONS_FAILURE =
  'changesync/projectDuplications/RETRIEVE_DUPLICATIONS_FAILURE';
const HIDE_DUPLICATION_REQUEST =
  'changesync/projectDuplications/HIDE_DUPLICATION_REQUEST';
const HIDE_DUPLICATION_SUCCESS =
  'changesync/projectDuplications/HIDE_DUPLICATION_SUCCESS';
const HIDE_DUPLICATION_FAILURE =
  'changesync/projectDuplications/HIDE_DUPLICATION_FAILURE';

const PROCESSING = 'processing';

// Initial State
const initialState = {
  items: [],
  retrieveDuplications: {
    pending: false,
    success: false,
    error: null,
  },
  /**
   * Grows whenever a duplication starts or finishes, so project lists
   * know to reload: the placeholder appears, turns into a project or disappears.
   */
  version: 0,
};

const hasFinishedSince = (previousItems, items) =>
  previousItems.some(
    (previous) =>
      previous.status === PROCESSING &&
      !items.some(
        (item) => item.id === previous.id && item.status === PROCESSING
      )
  );

// Reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case DUPLICATE_PROJECT_SUCCESS:
      return {
        ...state,
        items: [action.payload.duplication, ...state.items],
        version: state.version + 1,
      };
    case RETRIEVE_DUPLICATIONS_REQUEST:
      return {
        ...state,
        retrieveDuplications: {
          ...state.retrieveDuplications,
          pending: true,
          error: null,
        },
      };
    case RETRIEVE_DUPLICATIONS_SUCCESS:
      return {
        ...state,
        items: action.payload,
        retrieveDuplications: {
          pending: false,
          success: true,
          error: null,
        },
        version: hasFinishedSince(state.items, action.payload)
          ? state.version + 1
          : state.version,
      };
    case RETRIEVE_DUPLICATIONS_FAILURE:
      return {
        ...state,
        retrieveDuplications: {
          ...state.retrieveDuplications,
          pending: false,
          error: action.error,
        },
      };
    // Hidden optimistically: a failed hide comes back with the next retrieve.
    case HIDE_DUPLICATION_REQUEST:
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.meta.id),
      };
    default:
      return state;
  }
};

// Action Creators

/**
 * @param {number} projectId source project
 * @param {{ project: object, sections: string[], initiativeTypes: string[] }} payload
 */
export const duplicateProject = (projectId, payload) => ({
  [CALL_API]: {
    types: [
      DUPLICATE_PROJECT_REQUEST,
      DUPLICATE_PROJECT_SUCCESS,
      DUPLICATE_PROJECT_FAILURE,
    ],
    endpoint: `projects/${projectId}/duplicate`,
    method: HTTP_METHODS.POST,
    body: payload,
  },
});

export const retrieveProjectDuplications = () => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_DUPLICATIONS_REQUEST,
        RETRIEVE_DUPLICATIONS_SUCCESS,
        RETRIEVE_DUPLICATIONS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/project_duplications`,
      method: HTTP_METHODS.GET,
    },
  });
};

export const hideProjectDuplication = (id) => ({
  [CALL_API]: {
    types: [
      HIDE_DUPLICATION_REQUEST,
      HIDE_DUPLICATION_SUCCESS,
      HIDE_DUPLICATION_FAILURE,
    ],
    endpoint: `project_duplications/${id}/hide`,
    method: HTTP_METHODS.PUT,
  },
  meta: { id },
});

// Selectors

export const selectProjectDuplications = (state) =>
  state.projectDuplications.items;

export const selectProcessingDuplications = createSelector(
  selectProjectDuplications,
  (items) => items.filter((item) => item.status === PROCESSING)
);

export const selectFinishedDuplications = createSelector(
  selectProjectDuplications,
  (items) => items.filter((item) => item.status !== PROCESSING)
);

export const selectDuplicationsVersion = (state) =>
  state.projectDuplications.version;
